import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import LanguageSelector from "@/components/LanguageSelector";
import { Check, ArrowLeft, Sparkles, Zap, Crown, Building2, Rocket, Infinity } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f50ba61d-1f02-4136-b6b8-0e3c71c5b623/artifacts/e4hujy2b_sologo-aitools-bgremover-trimmed.png";

const plans = [
  {
    id: "solo",
    icon: Sparkles,
    priceMonthly: 0,
    priceYearly: 0,
    color: "#6B7280"
  },
  {
    id: "creator",
    icon: Zap,
    priceMonthly: 9.99,
    priceYearly: 99,
    color: "#20C997"
  },
  {
    id: "pro",
    icon: Crown,
    priceMonthly: 24.99,
    priceYearly: 249,
    popular: true,
    color: "#1FB6FF"
  },
  {
    id: "business",
    icon: Building2,
    priceMonthly: 69.99,
    priceYearly: 699,
    color: "#1E2A5A"
  },
  {
    id: "agency",
    icon: Rocket,
    priceMonthly: 99,
    priceYearly: 990,
    color: "#8B5CF6"
  },
  {
    id: "infinity",
    icon: Infinity,
    priceMonthly: 199,
    priceYearly: 1990,
    enterprise: true,
    color: "#F59E0B"
  }
];

export default function PricingPage() {
  const { t } = useTranslation();
  const [isYearly, setIsYearly] = useState(false);

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="pricing-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Diffuzo" className="h-8" />
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button variant="outline" className="rounded-xl border-gray-200 text-[#1E2A5A]" asChild>
              <Link to="/login">{t('auth.login.submit')}</Link>
            </Button>
            <Button className="bg-[#1E2A5A] hover:bg-[#20C997] rounded-xl" asChild>
              <Link to="/register">{t('pricing.getStarted')}</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#6B7280] hover:text-[#1E2A5A] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </Link>
          
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-[#1E2A5A] mb-4">
            {t('pricing.title')}
          </h1>
          <p className="text-lg text-[#6B7280] mb-8 max-w-2xl mx-auto">
            {t('pricing.subtitle')}
          </p>

          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <span className={`text-sm font-medium ${!isYearly ? 'text-[#1E2A5A]' : 'text-[#6B7280]'}`}>
              {t('pricing.monthly')}
            </span>
            <Switch
              checked={isYearly}
              onCheckedChange={setIsYearly}
              className="data-[state=checked]:bg-[#20C997]"
            />
            <span className={`text-sm font-medium ${isYearly ? 'text-[#1E2A5A]' : 'text-[#6B7280]'}`}>
              {t('pricing.yearly')}
            </span>
            {isYearly && (
              <Badge className="bg-[#20C997]/10 text-[#20C997] hover:bg-[#20C997]/10 rounded-full">
                -2 {t('pricing.monthly').toLowerCase()}
              </Badge>
            )}
          </div>

          {/* Plans grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {plans.map((plan) => {
              const Icon = plan.icon;
              const planData = t(`pricing.plans.${plan.id}`, { returnObjects: true });
              const price = isYearly ? plan.priceYearly : plan.priceMonthly;
              
              return (
                <div
                  key={plan.id}
                  className={`relative bg-white rounded-3xl p-8 soft-shadow hover-lift transition-all duration-300 ${
                    plan.popular ? 'ring-2 ring-[#1FB6FF]' : ''
                  } ${plan.enterprise ? 'ring-2 ring-[#F59E0B]' : ''}`}
                  data-testid={`plan-${plan.id}`}
                >
                  {plan.popular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1FB6FF] hover:bg-[#1FB6FF] rounded-full px-4">
                      {t('pricing.popular')}
                    </Badge>
                  )}
                  {plan.enterprise && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F59E0B] hover:bg-[#F59E0B] rounded-full px-4">
                      {t('pricing.enterprise')}
                    </Badge>
                  )}

                  {/* Icon */}
                  <div 
                    className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${plan.color}15` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: plan.color }} />
                  </div>

                  {/* Plan name */}
                  <h3 className="text-xl font-bold font-heading text-[#1E2A5A] mb-1">
                    {planData.name}
                  </h3>
                  <p className="text-sm text-[#6B7280] mb-4">
                    {planData.description}
                  </p>

                  {/* Price */}
                  <div className="mb-6">
                    {price === 0 ? (
                      <div className="text-3xl font-bold text-[#1E2A5A]">
                        {t('pricing.free')}
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-[#1E2A5A]">
                          {price}€
                        </span>
                        <span className="text-[#6B7280]">
                          {isYearly ? t('pricing.perYear') : t('pricing.perMonth')}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* CTA */}
                  <Button
                    className={`w-full rounded-xl mb-6 ${
                      plan.popular || plan.enterprise
                        ? 'bg-[#1E2A5A] hover:bg-[#20C997]'
                        : 'bg-white text-[#1E2A5A] border border-gray-200 hover:bg-[#F5F7FA]'
                    }`}
                    asChild
                  >
                    <Link to="/register">
                      {t('pricing.getStarted')}
                    </Link>
                  </Button>

                  {/* Features */}
                  <ul className="space-y-3 text-left">
                    {planData.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div 
                          className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ backgroundColor: `${plan.color}15` }}
                        >
                          <Check className="w-3 h-3" style={{ color: plan.color }} />
                        </div>
                        <span className="text-sm text-[#6B7280]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Yearly discount note */}
          <p className="mt-12 text-[#6B7280]">
            {t('pricing.yearlyDiscount')}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <img src={LOGO_URL} alt="Diffuzo" className="h-6 mx-auto mb-4 opacity-50" />
          <p className="text-sm text-[#6B7280]">
            © {new Date().getFullYear()} Diffuzo. {t('landing.footer.rights')}
          </p>
        </div>
      </footer>
    </div>
  );
}
